import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { SubdirectoryArrowRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import colors from "../../Constants/colors";
import {
  AppendixStructure,
  PredefinedSectionDocument,
} from "../../Models/appendixStructure";

interface PredefinedSectionPreviewModalProps {
  open: boolean;
  onClose: () => void;
  structure: AppendixStructure | null;
}

const SectionCard: React.FC<{
  item: PredefinedSectionDocument;
}> = ({ item }) => {
  const hasSubsections = item.subsections && item.subsections.length > 0;

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: `0 2px 8px rgba(3, 46, 77, 0.1)`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          backgroundColor: colors.gray50,
          borderBottom: hasSubsections ? `1px solid ${colors.gray200}` : "none",
        }}
      >
        <Chip
          label={item.order}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.75rem",
            backgroundColor: colors.accent,
            color: colors.white,
            minWidth: 24,
            height: 24,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: colors.primary_dark,
            flex: 1,
          }}
        >
          {item.name}
        </Typography>
        {hasSubsections && (
          <Chip
            label={`${item.subsections!.length} sub`}
            size="small"
            variant="outlined"
            sx={{
              fontSize: "0.7rem",
              height: 22,
              borderColor: colors.gray300,
              color: colors.gray500,
            }}
          />
        )}
      </Box>

      {hasSubsections && (
        <Box sx={{ px: 1, py: 1, backgroundColor: colors.white }}>
          {item.subsections!.map((sub, idx) => (
            <Box
              key={sub.id || idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                ml: 1,
                borderRadius: 1,
                transition: "background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: colors.gray50,
                },
              }}
            >
              <SubdirectoryArrowRight
                sx={{ fontSize: 16, color: colors.gray400 }}
              />
              <Chip
                label={sub.order}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  backgroundColor: colors.primary_dark,
                  color: colors.white,
                  minWidth: 36,
                  height: 22,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: colors.primary_dark,
                  flex: 1,
                }}
              >
                {sub.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const PredefinedSectionPreviewModal: React.FC<
  PredefinedSectionPreviewModalProps
> = ({ open, onClose, structure }) => {
  const { t } = useTranslation("workspaces");

  const totalSections = structure?.structure.length ?? 0;
  const totalSubsections =
    structure?.structure.reduce(
      (acc, s) => acc + (s.subsections?.length ?? 0),
      0,
    ) ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          sx={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: colors.primary_dark,
            borderBottom: `2px solid ${colors.accent}`,
            paddingBottom: "10px",
          }}
        >
          {t("predefinedSectionPreview")}
        </Typography>
        {structure && structure.structure.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
            <Chip
              label={`${totalSections} ${totalSections === 1 ? "section" : "sections"}`}
              size="small"
              sx={{
                backgroundColor: colors.orange50,
                color: colors.accent_dark,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
            {totalSubsections > 0 && (
              <Chip
                label={`${totalSubsections} ${totalSubsections === 1 ? "subsection" : "subsections"}`}
                size="small"
                sx={{
                  backgroundColor: colors.blue50,
                  color: colors.secondary,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {structure && structure.structure.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            {structure.structure.map((item, index) => (
              <SectionCard key={item.id || index} item={item} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: colors.gray400,
            }}
          >
            <Typography variant="body1">{t("noPredefinedSections")}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: colors.accent,
            color: colors.white,
            "&:hover": {
              backgroundColor: colors.primary_dark,
              color: colors.accent,
            },
          }}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PredefinedSectionPreviewModal;
